// PoliticaCookies.jsx
import React, { useEffect, useState } from "react";
import { Container, Typography, Divider, Box, Button } from "@mui/material";

export default function PoliticaCookies() {
    useEffect(() => {
        document.title = "Política de Cookies - EspacioCompacto";
        const meta = document.querySelector('meta[name="description"]');
        const description = "Política de cookies de EspacioCompacto: tipos de cookies usadas, finalidad y cómo gestionarlas (Cali, Colombia).";
        if (meta) {
            meta.setAttribute("content", description);
        } else {
            const m = document.createElement("meta");
            m.name = "description";
            m.content = description;
            document.head.appendChild(m);
        }

        const jsonLd = {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Política de Cookies - EspacioCompacto",
            "url": "https://espaciocompacto.com/politica-cookies"
        };
        const scriptId = "jsonld-politica-cookies";
        let script = document.getElementById(scriptId);
        if (script) script.remove();
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = scriptId;
        script.text = JSON.stringify(jsonLd);
        document.head.appendChild(script);

        return () => {
            const s = document.getElementById(scriptId);
            if (s) s.remove();
        };
    }, []);

    // UI-only helper to demonstrate control (no backend preference stored aquí)
    const [consent, setConsent] = useState(() => {
        try {
            return localStorage.getItem("espaciocompacto_cookies") === "accepted";
        } catch {
            return false;
        }
    });

    const handleAccept = () => {
        try {
            localStorage.setItem("espaciocompacto_cookies", "accepted");
        } catch {}
        setConsent(true);
    };

    const handleReject = () => {
        try {
            localStorage.setItem("espaciocompacto_cookies", "rejected");
        } catch {}
        setConsent(false);
    };

    return (
        <Container maxWidth="md" sx={{ py: 6, pt:16 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Política de Cookies
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
                EspacioCompacto — https://espaciocompacto.com
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">1. ¿Qué son las cookies?</Typography>
                <Typography paragraph>
                    Las cookies son pequeños ficheros que se almacenan en tu navegador cuando visitas un sitio web. Permiten recordar tus preferencias, mejorar la experiencia y, si se configura, recopilar información estadística de uso.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">2. ¿Qué cookies utilizamos?</Typography>
                <Typography paragraph>
                    Este sitio puede utilizar las siguientes categorías de cookies:
                </Typography>
                <ol>
                    <li><strong>Cookies estrictamente necesarias.</strong> Necesarias para el funcionamiento del formulario de contacto y la navegación básica.</li>
                    <li><strong>Cookies de rendimiento/analítica (opcionales).</strong> Permiten obtener estadísticas anónimas sobre el uso del sitio (por ejemplo Google Analytics). Estas se utilizarán solo si das tu consentimiento.</li>
                    <li><strong>Cookies funcionales (opcionales).</strong> Guardan tus preferencias para mejorar la experiencia (por ejemplo, idioma o tamaño de texto).</li>
                </ol>
                <Typography paragraph>
                    Nota: En la actualidad no se gestionan ventas online ni pagos que requieran cookies específicas de comercio electrónico.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">3. Duración de las cookies</Typography>
                <Typography paragraph>
                    Las cookies pueden ser de sesión (se borran al cerrar el navegador) o persistentes (permanecen un tiempo en tu dispositivo). La duración concreta depende de cada cookie y del servicio que la origine.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">4. Cómo gestionar y eliminar cookies</Typography>
                <Typography paragraph>
                    Puedes aceptar o rechazar cookies mediante las opciones que ofrecemos en el sitio. También puedes configurar tu navegador para bloquear o eliminar cookies (consulta la ayuda de tu navegador para instrucciones). Ten en cuenta que bloquear cookies puede afectar la funcionalidad del sitio.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">5. Consentimiento</Typography>
                <Typography paragraph>
                    Al continuar navegando en este sitio sin configurar tus preferencias, podríamos entender que aceptas el uso de cookies conforme a esta política. Recomendamos revisar y ajustar las preferencias si lo deseas.
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Preferencias de cookies (simulación en cliente)</Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Button variant={consent ? "contained" : "outlined"} onClick={handleAccept}>
                    Aceptar cookies
                </Button>
                <Button variant={!consent ? "contained" : "outlined"} onClick={handleReject}>
                    Rechazar cookies
                </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" paragraph>
                Estado actual: {consent ? "Has aceptado cookies (simulado)" : "No has aceptado cookies (simulado)"}.
            </Typography>

            <Box sx={{ mt: 4, p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
                <Typography variant="subtitle2">Aviso:</Typography>
                <Typography variant="body2">
                    Esta política explica las categorías generales de cookies. Si integras servicios de terceros (analítica, vídeos embebidos, redes sociales), revisa sus políticas específicas y añade detalle de las cookies concretas que se usan. Este texto es informativo y no sustituye asesoría legal.
                </Typography>
            </Box>
        </Container>
    );
}

